// Theme management utilities for dark mode support

export type Theme = 'light' | 'dark';

const THEME_STORAGE_KEY = 'flightcents-theme';
const THEME_CLASS = 'dark';

/**
 * Get the user's theme preference from localStorage or system preference
 */
export function getThemePreference(): Theme {
  try {
    const stored = localStorage.getItem(THEME_STORAGE_KEY);
    if (stored === 'light' || stored === 'dark') {
      return stored;
    }
  } catch (error) {
    console.error('Failed to read theme preference:', error);
  }

  // Check system preference
  if (typeof window !== 'undefined' && window.matchMedia) {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  }

  return 'light';
}

/**
 * Save theme preference to localStorage and apply to DOM
 */
export function setTheme(theme: Theme): void {
  try {
    localStorage.setItem(THEME_STORAGE_KEY, theme);
  } catch (error) {
    console.error('Failed to save theme preference:', error);
  }

  applyTheme(theme);
}

/**
 * Apply theme class to HTML element
 */
export function applyTheme(theme: Theme): void {
  const html = document.documentElement;
  if (theme === 'dark') {
    html.classList.add(THEME_CLASS);
  } else {
    html.classList.remove(THEME_CLASS);
  }
}

/**
 * Toggle between light and dark theme
 */
export function toggleTheme(): Theme {
  const current = getThemePreference();
  const next = current === 'light' ? 'dark' : 'light';
  setTheme(next);
  return next;
}

/**
 * Listen for system theme changes
 */
export function onSystemThemeChange(callback: (theme: Theme) => void): () => void {
  if (typeof window === 'undefined' || !window.matchMedia) {
    return () => {};
  }

  const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
  
  const handleChange = (e: MediaQueryListEvent) => {
    const theme = e.matches ? 'dark' : 'light';
    // Only apply if user hasn't set preference
    const stored = localStorage.getItem(THEME_STORAGE_KEY);
    if (!stored) {
      callback(theme);
    }
  };

  mediaQuery.addEventListener('change', handleChange);
  
  return () => mediaQuery.removeEventListener('change', handleChange);
}
